import { gql, useMutation } from '@apollo/client';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import {
  GET_USER_VALUES,
  GetUserValuesInput,
  GetUserValuesResult,
  UserValue,
  USERVALUE_FIELDS,
} from '@dieterApi/persistentState/useUserValuesQuery';

const ADD_USER_VALUE = gql`
  ${USERVALUE_FIELDS}
  mutation AddUserValue($key: String!, $value: String!, $index: String, $createIndex: Boolean, $token: String) {
    addUserValue(key: $key, value: $value, index: $index, createIndex: $createIndex, token: $token) {
      ...UserValueFields
    }
  }
`;

export interface UserValueAddInput {
  key: string;
  value: string;
  index?: string;
  createIndex?: boolean;
  token?: string;
}

export interface UserValueAddResult {
  addUserValue: UserValue;
}

export interface UserValueAddContext {
  userId: string;
  companyId: string;
}

export function useUserValueAddMutation() {
  const { user, activeCompany } = useUserWithCompany();
  return useMutation<UserValueAddResult, UserValueAddInput, UserValueAddContext>(ADD_USER_VALUE, {
    context: {
      userId: user?.id || '',
      companyId: activeCompany?.id || '',
    },
    update(cache, { data }, { context, variables }) {
      if (!data?.addUserValue) return;
      try {
        // Read the existing GetUserValues query from cache
        const existingData = cache.readQuery<GetUserValuesResult, GetUserValuesInput>({
          query: GET_USER_VALUES,
          variables: { companyId: context?.companyId || '' },
        });

        if (existingData?.getUserValues) {
          const newUserValue = data.addUserValue;
          const existingValues = existingData.getUserValues;

          // Check if this user value already exists (update case)
          const existingIndex = existingValues.findIndex(
            (uv) => uv.key === newUserValue.key && uv.index === newUserValue.index
          );

          let updatedValues: UserValue[];
          if (existingIndex >= 0) {
            // Update existing value
            updatedValues = [...existingValues];
            updatedValues[existingIndex] = newUserValue;
          } else {
            // Add new value
            updatedValues = [...existingValues, newUserValue];
          }
          // Write the updated list back to cache
          cache.writeQuery<GetUserValuesResult>({
            query: GET_USER_VALUES,
            variables: { companyId: context?.companyId || '' },
            data: {
              getUserValues: updatedValues,
            },
          });
        }
      } catch (error) {
        // Query might not be in cache yet, that's okay - it will be fetched when needed
        console.debug('GetUserValues query not in cache, skipping cache update');
      }
    },
  });
}
